import { Alert, Button, Card, Input, Modal, Space, Tag as AntTag, Typography } from "antd";
import { useEffect, useState } from "react";
import {
    Dialog as ZhDialog,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { TYPE_STATE } from "@/meta/manageCenter/tag";
import { useUserStore } from "@/views/stores";
import { UserTagsPage } from "./UserTagsPage";

export interface TagListRow {
    id: number;
    name: string;
}

export interface TagBusinessRow {
    id: number;
    type?: number;
    name: string;
}

interface TagPageResult<T> {
    list: T[];
    total: number;
}

export interface TagListPageProps {
    loadTags?: (query: { name: string; page: number; size: number }) => Promise<TagPageResult<TagListRow>>;
    loadBusinesses?: (query: { tagId: number; page: number; size: number }) => Promise<TagPageResult<TagBusinessRow>>;
    createTag?: (params: { name: string }) => Promise<unknown>;
    updateTag?: (id: number, params: { name: string }) => Promise<unknown>;
    deleteTag?: (id: number) => Promise<unknown>;
    removeTag?: (id: number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 标签分页接口。 */
export async function loadTags(query: { name: string; page: number; size: number }) {
    const response = await request.service.get<TagPageResult<TagListRow>>("/admin/tag/paging", { params: query });
    return response.data;
}

/** 复用 Vue 标签业务分页接口。 */
export async function loadTagBusinesses(query: { tagId: number; page: number; size: number }) {
    const response = await request.service.get<TagPageResult<TagBusinessRow>>("/admin/tag-business/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 标签新增接口。 */
export async function createTag(params: { name: string }) {
    const response = await request.service.post("/admin/tag", params);
    return response.data;
}

/** 复用 Vue 标签修改接口。 */
export async function updateTag(id: number, params: { name: string }) {
    const response = await request.service.put(`/admin/tag/${id}`, params);
    return response.data;
}

/** 复用 Vue 标签删除接口。 */
export async function deleteTag(id: number) {
    const response = await request.service.delete(`/admin/tag/${id}`);
    return response.data;
}

/** 复用 Vue 业务标签移除接口。 */
export async function removeTag(id: number) {
    const response = await request.service.delete(`/admin/tag-business/${id}`);
    return response.data;
}

/** 管理中心-标签列表，迁移 Vue 标签选择、业务列表和 CRUD 操作。 */
export function TagListPage({
    loadTags: loadTagRows = loadTags,
    loadBusinesses = loadTagBusinesses,
    createTag: saveTag = createTag,
    updateTag: editTag = updateTag,
    deleteTag: removeTagRow = deleteTag,
    removeTag: detachTag = removeTag,
    permissions
}: TagListPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_LABEL_W");
    const [tags, setTags] = useState<TagListRow[]>([]);
    const [selectedTag, setSelectedTag] = useState<TagListRow>();
    const [businessRows, setBusinessRows] = useState<TagBusinessRow[]>([]);
    const [businessTotal, setBusinessTotal] = useState(0);
    // 保留 Vue ProTable 当前页，确保业务标签翻页请求提交正确的 page 参数。
    const [businessPage, setBusinessPage] = useState(1);
    const [searchName, setSearchName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [validationMessage, setValidationMessage] = useState("");
    const [dialog, setDialog] = useState<"add" | "edit" | "delete">();
    const [tagName, setTagName] = useState("");
    const [dialogTag, setDialogTag] = useState<TagListRow>();
    const [businessToRemove, setBusinessToRemove] = useState<number>();
    const [saving, setSaving] = useState(false);
    const [businessType, setBusinessType] = useState<"tags" | "user-tags">("tags");

    const refreshTags = async (name = searchName) => {
        try {
            const result = await loadTagRows({ name, page: 1, size: 20 });
            setTags(result.list || []);
            setSelectedTag(current => result.list.find(tag => tag.id === current?.id) || result.list[0]);
            setBusinessPage(1);
            setErrorMessage("");
        } catch {
            setTags([]);
            setSelectedTag(undefined);
            setErrorMessage("标签列表加载失败");
        }
    };

    useEffect(() => {
        void refreshTags("");
        // 首次进入只沿用 Vue 的空关键字查询，后续由显式搜索触发。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadTagRows]);

    useEffect(() => {
        if (!selectedTag) {
            setBusinessRows([]);
            setBusinessTotal(0);
            return;
        }
        let disposed = false;
        void loadBusinesses({ tagId: selectedTag.id, page: businessPage, size: 20 })
            .then(result => {
                if (disposed) return;
                setBusinessRows(result.list || []);
                setBusinessTotal(result.total || 0);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("标签业务列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [businessPage, loadBusinesses, selectedTag]);

    const openAdd = () => {
        setDialog("add");
        setDialogTag(undefined);
        setTagName("");
        setValidationMessage("");
    };

    const openEdit = (tag: TagListRow) => {
        setDialog("edit");
        setDialogTag(tag);
        setTagName(tag.name);
        setValidationMessage("");
    };

    const openDelete = (tag: TagListRow) => {
        setDialog("delete");
        setDialogTag(tag);
    };

    const submitDialog = async () => {
        if (dialog !== "delete" && !tagName.trim()) {
            setValidationMessage("标签名称不可为空");
            return;
        }
        if (dialog === "delete" && !dialogTag) return;
        setSaving(true);
        try {
            if (dialog === "add") await saveTag({ name: tagName.trim() });
            if (dialog === "edit" && dialogTag) await editTag(dialogTag.id, { name: tagName.trim() });
            if (dialog === "delete" && dialogTag) await removeTagRow(dialogTag.id);
            setDialog(undefined);
            await refreshTags();
        } catch {
            setErrorMessage("标签操作失败");
        } finally {
            setSaving(false);
        }
    };

    const submitSearch = () => {
        if (!searchName || searchName.length >= 2) void refreshTags(searchName);
    };

    const detachBusinessTag = async (id: number) => {
        try {
            await detachTag(id);
            setBusinessToRemove(undefined);
            if (selectedTag) {
                const result = await loadBusinesses({ tagId: selectedTag.id, page: businessPage, size: 20 });
                setBusinessRows(result.list || []);
                setBusinessTotal(result.total || 0);
            }
        } catch {
            setErrorMessage("标签移除失败");
        }
    };

    // 业务标签移除沿用 Vue 页面的确认步骤，确认后才调用 DELETE 接口。
    const confirmBusinessRemoval = () => {
        if (businessToRemove !== undefined) void detachBusinessTag(businessToRemove);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 标签业务列表的类型、名称和移除操作。
    const businessColumns: TableColumnProps<TagBusinessRow>[] = [
        {
            key: "type",
            label: "类型",
            prop: "type",
            render: ({ row }) => <AntTag>{TYPE_STATE.getIntroByCode(row.type) || "--"}</AntTag>
        },
        { key: "name", label: "名称", prop: "name", render: ({ row }) => row.name },
        {
            key: "operation",
            label: "操作",
            prop: "id",
            render: ({ row }) =>
                canWrite ? (
                    <Button disabled={banButton} type="link" onClick={() => setBusinessToRemove(row.id)}>
                        移除
                    </Button>
                ) : null
        }
    ];

    return (
        <section aria-label="tag-list-page" className="flex min-h-full flex-col gap-4">
            <div aria-label="标签页签" role="tablist">
                <Button aria-selected={businessType === "tags"} role="tab" onClick={() => setBusinessType("tags")}>
                    标签列表
                </Button>
                <Button
                    aria-selected={businessType === "user-tags"}
                    role="tab"
                    onClick={() => setBusinessType("user-tags")}
                >
                    用户标签
                </Button>
            </div>
            {businessType === "user-tags" ? (
                <UserTagsPage permissions={currentPermissions} />
            ) : (
                <Card>
                    <Typography.Title className="mb-4 mt-0" level={4}>
                        标签列表
                    </Typography.Title>
                    {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                    <Space className="mb-4">
                        <Input
                            aria-label="搜索标签"
                            placeholder="搜索标签"
                            value={searchName}
                            onChange={event => setSearchName(event.target.value)}
                            onPressEnter={submitSearch}
                        />
                        <Button aria-label="搜索" onClick={submitSearch}>
                            搜索
                        </Button>
                        {canWrite ? (
                            <Button type="primary" onClick={openAdd}>
                                新增
                            </Button>
                        ) : null}
                    </Space>
                    <div className="grid grid-cols-4 gap-4">
                        <div aria-label="标签选项" className="flex flex-col gap-2" role="listbox">
                            {tags.map(tag => (
                                <div className="flex items-center gap-2" key={tag.id}>
                                    <Button
                                        aria-label={tag.name}
                                        aria-selected={selectedTag?.id === tag.id}
                                        className="flex-1 text-left"
                                        onClick={() => setSelectedTag(tag)}
                                        type={selectedTag?.id === tag.id ? "primary" : "default"}
                                    >
                                        {tag.name}
                                    </Button>
                                    {canWrite ? (
                                        <>
                                            <Button
                                                aria-label={`修改${tag.name}`}
                                                onClick={() => openEdit(tag)}
                                                type="link"
                                            >
                                                修改
                                            </Button>
                                            <Button
                                                aria-label={`删除${tag.name}`}
                                                onClick={() => openDelete(tag)}
                                                type="link"
                                            >
                                                删除
                                            </Button>
                                        </>
                                    ) : null}
                                </div>
                            ))}
                            {!tags.length ? <Typography.Text type="secondary">暂无标签</Typography.Text> : null}
                        </div>
                        <div className="col-span-3">
                            <ZhTable<TagBusinessRow>
                                data={businessRows}
                                columns={businessColumns}
                                emptyText="暂无数据"
                                pagination={false}
                                rowKey={row => String(row.id)}
                                isShowResetBtn={false}
                                isShowSearchBtn={false}
                                tableHeader={
                                    <div className="admin-table-composed-header">
                                        <Typography.Title className="mt-0" level={5}>
                                            {selectedTag?.name || ""}
                                            {selectedTag && businessTotal ? ` (${businessTotal})` : ""}
                                        </Typography.Title>
                                    </div>
                                }
                                append={
                                    <div className="admin-table-composed-pagination">
                                        <ZhPagination
                                            current={businessPage}
                                            pageSize={20}
                                            total={businessTotal}
                                            hideOnSinglePage
                                            onChange={setBusinessPage}
                                        />
                                    </div>
                                }
                            />
                        </div>
                    </div>
                </Card>
            )}
            <ZhDialog
                title={dialog === "add" ? "新增标签" : dialog === "edit" ? "修改标签" : "是否删除"}
                open={dialog != null}
                width={500}
                onCancel={() => setDialog(undefined)}
                footer={
                    <Space>
                        <Button onClick={() => setDialog(undefined)}>取消</Button>
                        <Button
                            aria-label={dialog === "delete" ? "确定" : "保存"}
                            loading={saving}
                            type="primary"
                            onClick={() => void submitDialog()}
                        >
                            {dialog === "delete" ? "确定" : "保存"}
                        </Button>
                    </Space>
                }
            >
                {dialog === "delete" ? (
                    <Typography.Text>删除后此项信息将不再显示</Typography.Text>
                ) : (
                    <Space direction="vertical" className="w-full">
                        <Input
                            aria-label="标签名称"
                            maxLength={10}
                            placeholder="请输入标签名称"
                            value={tagName}
                            onChange={event => {
                                setTagName(event.target.value);
                                setValidationMessage("");
                            }}
                        />
                        {validationMessage ? (
                            <Typography.Text aria-live="polite" type="danger">
                                {validationMessage}
                            </Typography.Text>
                        ) : null}
                    </Space>
                )}
            </ZhDialog>
            <Modal
                title="是否移除"
                open={businessToRemove !== undefined}
                onCancel={() => setBusinessToRemove(undefined)}
                footer={
                    <Space>
                        <Button onClick={() => setBusinessToRemove(undefined)}>取消</Button>
                        <Button aria-label="确定" type="primary" onClick={confirmBusinessRemoval}>
                            确定
                        </Button>
                    </Space>
                }
            >
                <Typography.Text>移除后此业务将不再关联当前标签</Typography.Text>
            </Modal>
        </section>
    );
}

export default TagListPage;
